import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../lib/api';
import { toast } from 'sonner';
import projectsService, { Project } from '../services/projects.service';

interface User {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  picture?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
  selectedProjectId: number | null;
  currentProject: Project | null;
  
  // Methods
  signInWithGoogle: (googleToken: string) => Promise<void>;
  signOut: () => void;
  fetchUser: () => Promise<void>;
  setSelectedProjectId: (projectId: number | null) => Promise<void>;
  fetchSelectedProject: () => Promise<void>;
}

const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isLoading: false,
      error: null,
      selectedProjectId: null,
      currentProject: null,
      
      signInWithGoogle: async (googleToken: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/google', { token: googleToken });
          
          if (response.data.status === 'success') {
            const { token, user } = response.data.data;
            
            // Store token in localStorage (the interceptor will use this)
            localStorage.setItem('auth_token', token);
            
            set({ token, user, isLoading: false });
            toast.success('Successfully signed in!');
          } else {
            throw new Error(response.data.message || 'Authentication failed');
          }
        } catch (error) {
          console.error('Google authentication error:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Authentication failed', 
            isLoading: false 
          });
          toast.error('Authentication failed. Please try again.');
        }
      },
      
      signOut: () => {
        localStorage.removeItem('auth_token');
        set({ user: null, token: null, selectedProjectId: null, currentProject: null });
        toast.info('You have been signed out');
      },
      
      fetchUser: async () => {
        const { token } = get();
        if (!token) return;
        
        set({ isLoading: true });
        try {
          const response = await api.get('/auth/me');
          
          if (response.data.status === 'success') {
            set({ user: response.data.data.user, isLoading: false });
          } else {
            throw new Error(response.data.message || 'Failed to fetch user data');
          }
        } catch (error) {
          console.error('Error fetching user:', error);
          // If we get a 401, clear the auth state
          if (error.response?.status === 401) {
            get().signOut();
          }
          
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch user data', 
            isLoading: false 
          });
        }
      },

      setSelectedProjectId: async (projectId: number | null) => {
        set({ selectedProjectId: projectId });
        
        if (projectId) {
          try {
            const response = await projectsService.getProject(projectId);
            if (response.status === 'success' && response.data) {
              set({ currentProject: response.data });
            }
          } catch (error) {
            console.error('Error fetching project:', error);
            set({ currentProject: null });
          }
        } else {
          set({ currentProject: null });
        }
      },

      fetchSelectedProject: async () => {
        const { selectedProjectId } = get();
        if (!selectedProjectId) return;

        try {
          const response = await projectsService.getProject(selectedProjectId);
          if (response.status === 'success' && response.data) {
            set({ currentProject: response.data });
          } else {
            set({ currentProject: null, selectedProjectId: null });
          }
        } catch (error) {
          console.error('Error fetching selected project:', error);
          set({ currentProject: null, selectedProjectId: null });
        }
      }
    }),
    {
      name: 'auth-storage',
      // Only persist these fields
      partialize: (state) => ({ 
        token: state.token,
        user: state.user,
        selectedProjectId: state.selectedProjectId
      }),
    }
  )
);

export default useAuthStore;